import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/Input';
import { Select, type SelectOption } from '@/components/ui/Select';
import { cn } from '@/lib/cn';

const CREATE_VALUE = '__create__';

type Props = {
  value: string;
  onValueChange: (value: string) => void;
  owners: string[];
  ownerLabels?: Record<string, string>;
  disabled?: boolean;
  className?: string;
  allowCreate?: boolean;
  placeholder?: string;
  'aria-label'?: string;
};

export function DebtOwnerSelect({
  value,
  onValueChange,
  owners,
  ownerLabels,
  disabled,
  className,
  allowCreate = true,
  placeholder,
  'aria-label': ariaLabel
}: Props) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  const inSavedList = owners.some((o) => o.toLowerCase() === value.trim().toLowerCase());
  const showCreateInput = allowCreate && (creating || owners.length === 0);

  const options = useMemo((): SelectOption[] => {
    const base: SelectOption[] = owners.map((owner) => ({
      value: owner,
      label: ownerLabels?.[owner] ?? owner
    }));
    if (allowCreate) {
      base.push({
        value: CREATE_VALUE,
        label: t('cashbook.debts.addNewOwner')
      });
    }
    return base;
  }, [owners, ownerLabels, allowCreate, t]);

  if (showCreateInput) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <Input
          className="w-full"
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
          placeholder={t('cashbook.debts.newOwnerPlaceholder')}
          aria-label={ariaLabel ?? t('cashbook.debts.creditor')}
        />
        {owners.length > 0 ? (
          <button
            type="button"
            className="text-left text-xs text-[var(--fv-primary)] hover:underline disabled:opacity-50"
            disabled={disabled}
            onClick={() => {
              setCreating(false);
              if (value && !inSavedList) onValueChange('');
            }}
          >
            {t('cashbook.debts.selectExistingOwner')}
          </button>
        ) : null}
      </div>
    );
  }

  const selectValue = inSavedList ? value : '';

  return (
    <Select
      className={className}
      value={selectValue}
      onValueChange={(next) => {
        if (next === CREATE_VALUE) {
          setCreating(true);
          onValueChange('');
          return;
        }
        setCreating(false);
        onValueChange(next);
      }}
      options={options}
      disabled={disabled}
      placeholder={placeholder ?? t('cashbook.debts.selectOwner')}
      aria-label={ariaLabel ?? t('cashbook.debts.creditor')}
    />
  );
}
